import { planCdtLevels, type CdtLevelPlan, type CdtLevelPlanOptions } from "./cdtLevelPlan";
import {
  effectiveAttrs,
  isPlaceholderValue,
  levelsForScope,
  type SpineLevel,
  type StudioDraft,
  type StudioItem,
  type StudioRunGeometry,
} from "./studioTypes";

export type CompressionStrategy =
  | { mode?: "tree" }
  | { mode: "measured"; options?: CdtLevelPlanOptions };

export const planCompression = (
  items: readonly StudioItem[],
  draft: StudioDraft,
  runs: readonly StudioRunGeometry[] = [],
  strategy: CompressionStrategy = {},
): CdtLevelPlan => {
  if (strategy.mode === "measured") return planCdtLevels(items, draft, runs, strategy.options);
  const declared = levelsForScope(draft.spine, draft.scopedLevelOrder, []);
  const root = declared[0];
  const branches = root
    ? [...new Set(items.map((item) => String(effectiveAttrs(item, draft)[root.attr] ?? "")))].map(
        (value) => {
          const branchItems = items.filter(
            (item) => String(effectiveAttrs(item, draft)[root.attr] ?? "") === value,
          );
          const ordered = levelsForScope(draft.spine, draft.scopedLevelOrder, [value]).filter(
            (level) =>
              level.attr === root.attr ||
              branchItems.some(
                (item) =>
                  !isPlaceholderValue(String(effectiveAttrs(item, draft)[level.attr] ?? "")),
              ),
          );
          const tiers = [
            ordered.slice(0, 1),
            ordered.slice(1, 2),
            ordered.slice(2, 3),
            ordered.slice(3),
          ].map((levels) => levels.map((level) => level.attr));
          return {
            value,
            skus: items.filter(
              (item) => String(effectiveAttrs(item, draft)[root.attr] ?? "") === value,
            ).length,
            tiers,
            because: [
              "Preserve tree CDT0",
              "Preserve tree CDT1",
              "Preserve next tree level",
              "Retain remaining levels in tree order",
            ],
          };
        },
      )
    : [];
  const levels: CdtLevelPlan["levels"] = [0, 1, 2, 3].map((tier) => {
    const attrs = new Set(branches.flatMap((branch) => branch.tiers[tier]));
    const sources: SpineLevel[] = declared.filter((level) => attrs.has(level.attr));
    return {
      levels: sources,
      orient: tier === 2 ? "vertical" : "horizontal",
      role: tier < 2 ? "vertical" : "horizontal",
      because:
        tier < 2
          ? "Preserve tree grouping"
          : "Compress remaining tree levels without dropping attributes",
    };
  });
  return {
    levels,
    branches,
    verticality: [],
    bandedness: [],
    needsRuling: [],
    demoted: [],
    inert: [],
    warnings: [],
  };
};
